import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);

// Neutral (zinc) ramp per mode. Dark == the original app look; light is the mirror.
// 950/900/800 are overridden by the theme's background/surface/border palette.
const RAMP = {
  dark: { 50: "#fafafa", 100: "#f4f4f5", 200: "#e4e4e7", 300: "#d4d4d8", 400: "#a1a1aa",
          500: "#71717a", 600: "#52525b", 700: "#3f3f46", 800: "#27272a", 900: "#18181b", 950: "#09090b" },
  light: { 50: "#09090b", 100: "#18181b", 200: "#27272a", 300: "#3f3f46", 400: "#52525b",
           500: "#71717a", 600: "#8b8b93", 700: "#c4c4c8", 800: "#e4e4e7", 900: "#f4f4f5", 950: "#ffffff" },
};

function hexToRgb(hex) {
  if (!hex) return null;
  let h = String(hex).replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((x) => x + x).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function lighten(hex, amt) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const mix = (v) => Math.round(v + (255 - v) * amt);
  const to2 = (v) => v.toString(16).padStart(2, "0");
  return `#${to2(mix(c.r))}${to2(mix(c.g))}${to2(mix(c.b))}`;
}
function darken(hex, amt) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const mix = (v) => Math.round(v * (1 - amt));
  const to2 = (v) => v.toString(16).padStart(2, "0");
  return `#${to2(mix(c.r))}${to2(mix(c.g))}${to2(mix(c.b))}`;
}

// Push a theme config (in a given light/dark mode) onto :root as CSS custom properties.
export function applyThemeVars(config, mode = "dark") {
  if (!config) return;
  const root = document.documentElement;
  const pal = (config.palettes || {})[mode] || (config.palettes || {}).dark || {};

  // Neutral ramp (background/surface/border come from the palette).
  const ramp = { ...RAMP[mode === "light" ? "light" : "dark"] };
  if (pal.background) ramp[950] = pal.background;
  if (pal.surface) ramp[900] = pal.surface;
  if (pal.border) ramp[800] = pal.border;
  Object.entries(ramp).forEach(([k, v]) => {
    root.style.setProperty(`--zinc-${k}`, v);
    const c = hexToRgb(v);
    if (c) root.style.setProperty(`--zinc-${k}-rgb`, `${c.r} ${c.g} ${c.b}`);
  });
  root.style.setProperty("--app-bg", ramp[950]);

  // Accent scale from the primary colour.
  const primary = pal.primary;
  const rgb = hexToRgb(primary);
  if (primary && rgb) {
    root.style.setProperty("--accent-rgb", `${rgb.r} ${rgb.g} ${rgb.b}`);
    root.style.setProperty("--accent-600", primary);
    root.style.setProperty("--accent-500", lighten(primary, 0.08));
    if (mode === "light") {
      // accent-200/300/400 are used ONLY for text/icons — in light mode they sit
      // on pale accent chips, so darken (not lighten) so the text stays readable.
      root.style.setProperty("--accent-400", darken(primary, 0.05));
      root.style.setProperty("--accent-300", darken(primary, 0.15));
      root.style.setProperty("--accent-200", darken(primary, 0.22));
    } else {
      root.style.setProperty("--accent-400", lighten(primary, 0.22));
      root.style.setProperty("--accent-300", lighten(primary, 0.38));
      root.style.setProperty("--accent-200", lighten(primary, 0.52));
    }
  }
  if (pal.secondary) root.style.setProperty("--color-secondary", pal.secondary);

  // Typography + radius.
  const t = config.typography || {};
  if (t.heading) root.style.setProperty("--font-heading", t.heading);
  if (t.body) root.style.setProperty("--font-body", t.body);
  if (t.base) root.style.setProperty("--font-base", `${t.base}px`);
  if (typeof config.radius === "number") root.style.setProperty("--radius", `${config.radius}px`);
  const s = config.spacing || {};
  if (s.base) root.style.setProperty("--space-base", `${s.base}px`);

  // Mode class drives shadcn's :root/.dark semantic tokens.
  root.classList.toggle("dark", mode !== "light");
  root.classList.toggle("light", mode === "light");
}

export function ThemeProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [mode, setModeState] = useState("light");
  const siteRef = useRef(localStorage.getItem("mh_site") || "");

  const load = useCallback((siteId, m) => {
    api.get("/theme_design/active", { params: { site_id: siteId || undefined } })
      .then((r) => { setConfig(r.data.config); applyThemeVars(r.data.config, m); })
      .catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("mh_theme_mode", "light"); load(siteRef.current, "light"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload the active site's theme when the Active Site selector changes.
  useEffect(() => {
    const handler = (e) => { siteRef.current = (e.detail || ""); load(siteRef.current, mode); };
    window.addEventListener("mh:site", handler);
    return () => window.removeEventListener("mh:site", handler);
  }, [load, mode]);

  const setMode = useCallback((m) => {
    setModeState(m);
    localStorage.setItem("mh_theme_mode", m);
    setConfig((c) => { if (c) applyThemeVars(c, m); return c; });
  }, []);

  // Called by the Theme Design editor to preview/publish live.
  const applyTheme = useCallback((cfg, m) => {
    setConfig(cfg);
    applyThemeVars(cfg, m || localStorage.getItem("mh_theme_mode") || "dark");
  }, []);

  const reload = useCallback(() => load(siteRef.current, "light"), [load]);

  return (
    <ThemeContext.Provider value={{ config, mode, setMode, applyTheme, reload, siteId: siteRef.current }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
